// src/hooks/useScenarios.js - stress scenarios and the out-of-sample backtest.
//
// One call returns both; the result is cached in localStorage for an hour,
// keyed by user and holdings, like the other quant hooks.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchScenarios } from '../api';
import { holdingsSignature, readCache, writeCache } from './useQuantAnalysis';

const useScenarios = (user, userProfile) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rateLimitInfo, setRateLimitInfo] = useState(null);
  const [hydratedKey, setHydratedKey] = useState(null);

  const hasMinHoldings = (userProfile?.holdings?.length || 0) >= 1;

  const cacheKey = useMemo(() => {
    if (!user?.uid || !userProfile?.holdings) return null;
    return `dd_scen_${user.uid}_${holdingsSignature(userProfile.holdings)}`;
  }, [user?.uid, userProfile?.holdings]);

  useEffect(() => {
    if (!cacheKey) return;
    const cached = readCache(cacheKey);
    if (cached) {
      setData(cached.data);
      if (cached.data?.usage) setRateLimitInfo(cached.data.usage);
    } else {
      setData(null);
    }
    setHydratedKey(cacheKey);
  }, [cacheKey]);

  const run = useCallback(async (force = false) => {
    if (!force && data) return data;
    if (!hasMinHoldings) return null;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchScenarios({
        holdings: userProfile.holdings,
        user_profile: { ...userProfile, isPremium: user?.isPremium || false },
        user_id: user?.uid || null,
      });
      setData(res);
      if (cacheKey) writeCache(cacheKey, res);
      if (res.usage) setRateLimitInfo(res.usage);
      return res;
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        setError(detail?.message || 'Daily scenario limit reached');
        setRateLimitInfo({ remaining: 0, daily_limit: detail?.daily_limit || 5, user_tier: detail?.user_tier || 'free' });
      } else {
        setError((typeof detail === 'string' ? detail : detail?.message) || err.message || 'Could not run the scenarios');
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [data, hasMinHoldings, userProfile, user, cacheKey]);

  return {
    scenarios: data,
    scenariosLoading: loading,
    scenariosError: error,
    scenariosRateLimitInfo: rateLimitInfo,
    scenariosHydrated: cacheKey !== null && hydratedKey === cacheKey,
    fetchScenarios: () => run(false),
    refreshScenarios: () => run(true),
    hasMinHoldings,
  };
};

export default useScenarios;
